/**
 * Shared pure-math scene-geometry builders (DEFUSE — clone elimination).
 *
 * Each per-kind builder takes the already-extracted NUMERIC / `Vec3` inputs
 * (NOT an `EntityPreview`, NOT a `DrawingEntity`) and returns the `{ geometry,
 * bounds }` pair that both the legacy `<kind>ToRenderable.ts` and the typed
 * `typed/<kind>FromEntity.ts` adapter feed into `buildRenderableShell`.
 *
 * Why this module exists: the legacy adapter (reads the `EntityPreview`
 * `properties` string bag) and its typed sibling (reads canonical
 * `DrawingEntity` fields) were byte-identical AFTER value extraction — the
 * SAME geometry build + the SAME cardinal-crossing bounds + an identical
 * `isAngleInArc`. That is one semantic truth (the render-geometry math),
 * duplicated only because the two paths differ in their ~3-line value source.
 * The math now lives here ONCE; each adapter is reduced to its value
 * extraction + a single builder call.
 *
 * `applyOffset` is applied INSIDE the builders for the absolute-position inputs
 * (line endpoints, point/circle/arc/ellipse centres) — exactly as both
 * siblings did. The ellipse `majorAxisRaw` is a vector RELATIVE to the centre
 * and is therefore NOT offset (matching both siblings).
 *
 * Pure: no `EntityPreview` / `DrawingEntity` import, no side effects beyond
 * reading the locked-in coordinate origin via `applyOffset`. Survives the S11
 * cutover unchanged — only the legacy bag-extraction wrappers are deleted.
 */

import { applyOffset } from "../coordinateOrigin";
import type { SceneGeometry, Vec3 } from "../types";

import { boundsFromPoints } from "./adapterContext";
import { tessellateEllipse } from "./arcEllipseSpline";

/** Degrees → radians (DXF ARC stores `startAngle` / `endAngle` in degrees). */
const DEG_TO_RAD = Math.PI / 180;

/** Default PDSIZE (5% of viewport per AutoCAD spec). Single home for both
 * the legacy bag adapter and the typed adapter. */
const DEFAULT_PDSIZE = 0;
/** Default PDMODE — single dot. Single home for both adapters. */
const DEFAULT_PDMODE = 0;

/** Bounds shape carried on `RenderableEntity.bounds`. */
export interface SceneBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** `{ geometry, bounds }` pair every builder returns. */
export interface SceneGeometryResult {
  geometry: SceneGeometry;
  bounds: SceneBounds;
}

/**
 * Build the `line` geometry + AABB from the raw (pre-offset) start / end
 * points. `applyOffset` is applied to BOTH endpoints (identical to both
 * siblings).
 */
export function buildLineSceneGeometry(
  aRaw: Vec3,
  bRaw: Vec3,
): SceneGeometryResult {
  const a = applyOffset(aRaw);
  const b = applyOffset(bRaw);

  const geometry: SceneGeometry = { kind: "line", a, b };

  const bounds: SceneBounds = {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y),
  };

  return { geometry, bounds };
}

/**
 * Build the `point` geometry + AABB from the raw (pre-offset) position and the
 * active PDMODE / PDSIZE (each `undefined` → the AutoCAD default, resolved
 * here so the defaults live in one home). The PDMODE bitset + PDSIZE are baked
 * onto the geometry so the renderer can pick the marker prototype without
 * re-parsing.
 *
 * When PDSIZE = 0 we fall back to a small fixed bbox of 1 world unit so the
 * spatial index can still hit-test the point (identical to both siblings).
 */
export function buildPointSceneGeometry(
  positionRaw: Vec3,
  pdmodeRaw: number | undefined,
  pdsizeRaw: number | undefined,
): SceneGeometryResult {
  const position = applyOffset(positionRaw);
  const pdmode = pdmodeRaw ?? DEFAULT_PDMODE;
  const pdsize = pdsizeRaw ?? DEFAULT_PDSIZE;

  const geometry: SceneGeometry = {
    kind: "point",
    position,
    pdmode,
    pdsize,
  };

  // Effective marker radius (used for bbox even though the renderer sizes the
  // screen-space marker at draw time). When PDSIZE = 0 we fall back to a small
  // fixed bbox of 1 world unit so the spatial index can still hit-test.
  const half = pdsize > 0 ? pdsize : pdsize < 0 ? Math.abs(pdsize) : 1;
  const bounds: SceneBounds = {
    minX: position.x - half,
    minY: position.y - half,
    maxX: position.x + half,
    maxY: position.y + half,
  };

  return { geometry, bounds };
}

/**
 * Build the parametric `circle` geometry + AABB from the raw (pre-offset)
 * centre and radius. The renderer tessellates per camera zoom (LOD), so no
 * fixed segment count is baked here.
 */
export function buildCircleSceneGeometry(
  centerRaw: Vec3,
  radius: number,
): SceneGeometryResult {
  const center = applyOffset(centerRaw);

  const geometry: SceneGeometry = { kind: "circle", center, radius };

  const bounds: SceneBounds = {
    minX: center.x - radius,
    minY: center.y - radius,
    maxX: center.x + radius,
    maxY: center.y + radius,
  };

  return { geometry, bounds };
}

/**
 * Build the `arc` geometry + AABB from the raw (pre-offset) centre, radius and
 * the start / end angles **in degrees** (the DXF ARC unit). Does the single
 * `* DEG_TO_RAD` conversion, then computes the bounds by sampling the start /
 * end points plus any 0/90/180/270° cardinal crossings that fall inside the
 * CCW arc sweep — the same approach `entityBounds.ts` uses, for parity.
 */
export function buildArcSceneGeometry(
  centerRaw: Vec3,
  radius: number,
  startDeg: number,
  endDeg: number,
): SceneGeometryResult {
  const center = applyOffset(centerRaw);
  const start = startDeg * DEG_TO_RAD;
  const end = endDeg * DEG_TO_RAD;

  const geometry: SceneGeometry = {
    kind: "arc",
    center,
    radius,
    start,
    end,
    ccw: true, // DXF ARC is always CCW from start → end (Autodesk DXF reference)
  };

  // Bounds: include start, end, and cardinal axis crossings inside the sweep.
  const startPoint: Vec3 = {
    x: center.x + radius * Math.cos(start),
    y: center.y + radius * Math.sin(start),
    z: center.z,
  };
  const endPoint: Vec3 = {
    x: center.x + radius * Math.cos(end),
    y: center.y + radius * Math.sin(end),
    z: center.z,
  };
  const candidatePoints: Vec3[] = [startPoint, endPoint];
  for (const angle of [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]) {
    if (isAngleInArc(angle, start, end)) {
      candidatePoints.push({
        x: center.x + radius * Math.cos(angle),
        y: center.y + radius * Math.sin(angle),
        z: center.z,
      });
    }
  }
  const bounds = boundsFromPoints(candidatePoints);

  return { geometry, bounds };
}

/**
 * Build the `ellipse` geometry + AABB from the raw (pre-offset) centre, the
 * major-axis endpoint (a vector RELATIVE to the centre — NOT offset), the
 * minor/major `ratio`, and the start / end parameters **in radians** (the
 * ELLIPSE unit; no conversion). Bounds are sampled at low LOD (32 samples) so
 * a partial-arc ellipse matches the renderer's tessellation exactly.
 */
export function buildEllipseSceneGeometry(
  centerRaw: Vec3,
  majorAxisRaw: Vec3,
  ratio: number,
  startParam: number,
  endParam: number,
  // SL-15 F-C — OCS extrusion normal (DXF 210/220/230). Fixes the minor-axis
  // DIRECTION so a tilted major axis renders in its true plane. Absent ⇒ the
  // (0,0,1) WCS default (planar ellipse). Carried onto the geometry so the LIVE
  // sampler (`geometryToLineVertices`) and this bounds pass agree.
  extrusion?: Vec3,
): SceneGeometryResult {
  const center = applyOffset(centerRaw);
  // Major-axis endpoint is a vector relative to centre → NOT offset.
  const majorAxis = majorAxisRaw;

  const geometry: SceneGeometry = {
    kind: "ellipse",
    center,
    majorAxis,
    ratio,
    start: startParam,
    end: endParam,
    ...(extrusion ? { extrusion } : {}),
  };

  // Bounds: sample the ellipse at low LOD (32 samples) — full ellipse bbox is
  // computable analytically, but for partial-arc ellipses the sampled approach
  // matches the renderer's tessellation exactly. Pass the SAME extrusion so the
  // 3D-tilted bounds match the tilted render.
  const samples = tessellateEllipse(
    center,
    majorAxis,
    ratio,
    startParam,
    endParam,
    32,
    extrusion,
  );
  const bounds = boundsFromPoints(samples);

  return { geometry, bounds };
}

/**
 * Is `angle` inside the CCW sweep from `start` to `end`? Single home for the
 * arc cardinal-crossing test shared by the legacy + typed arc adapters.
 */
function isAngleInArc(angle: number, start: number, end: number): boolean {
  const TWO_PI = Math.PI * 2;
  const norm = (a: number) => ((a % TWO_PI) + TWO_PI) % TWO_PI;
  const a = norm(angle);
  const s = norm(start);
  let e = norm(end);
  if (e < s) e += TWO_PI;
  let aShifted = a;
  if (aShifted < s) aShifted += TWO_PI;
  return aShifted >= s && aShifted <= e;
}
